import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl, uploadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { APP_LABELS, MAX_PRODUCTS } from "@/lib/constants";
import { ocrToProductPatch } from "@/lib/ocrMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import FileUpload from "@/components/FileUpload";
import CoverPreview, { PILL_STYLES } from "@/components/CoverPreview";
import CataloguePreview from "@/components/CataloguePreview";
import ProductEditor from "@/components/ProductEditor";
import ShareSheet from "@/components/ShareSheet";
import { toast } from "sonner";
import {
  Loader2, Save, Download, Share2, CheckCircle2, AlertTriangle, Plus, Library,
  ChevronUp, ChevronDown, Trash2, Copy, Pencil, GripVertical, FileDown, Images, MessageCircle, ImageIcon,
  History, RotateCw, RotateCcw, Crop, ShieldAlert, Lock, RefreshCw, Files, ImagePlus, Sparkles,
} from "lucide-react";

function fieldsComplete(p) {
  return (p.garden || p.name) && p.grade && p.liquor_image_file_id && p.report_file_id;
}

const MATCH_DOT = {
  green: { cls: "bg-emerald-500", label: "Matching" },
  amber: { cls: "bg-amber-500", label: "Info missing" },
  red: { cls: "bg-red-500", label: "Possible mismatch" },
};

export default function CatalogueEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [cat, setCat] = useState(null);
  const [brand, setBrand] = useState(null);
  const [saveState, setSaveState] = useState("saved"); // saved | dirty | saving
  const [validation, setValidation] = useState(null);
  const [editing, setEditing] = useState(null); // {product, index}
  const [libOpen, setLibOpen] = useState(false);
  const [library, setLibrary] = useState([]);
  const [libGrade, setLibGrade] = useState("all");
  const [libSort, setLibSort] = useState("price_asc");
  const [libSel, setLibSel] = useState(new Set());
  const [savedCovers, setSavedCovers] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [coverAdjust, setCoverAdjust] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [overrideFor, setOverrideFor] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoDraft, setPhotoDraft] = useState([]);

  const saveTimer = useRef(null);
  const catRef = useRef(null);
  catRef.current = cat;

  const libGrades = useMemo(
    () => Array.from(new Set(library.map((l) => (l.grade || "").trim()).filter(Boolean))).sort(),
    [library]
  );
  const libView = useMemo(() => {
    const arr = library.filter((l) => libGrade === "all" || (l.grade || "").trim() === libGrade);
    const pv = (l) => (l.price_per_kg == null || l.price_per_kg === "" ? null : Number(l.price_per_kg));
    return arr.slice().sort((a, b) => {
      const pa = pv(a), pb = pv(b);
      if (pa == null && pb == null) return 0;
      if (pa == null) return 1;
      if (pb == null) return -1;
      return libSort === "price_desc" ? pb - pa : pa - pb;
    });
  }, [library, libGrade, libSort]);

  useEffect(() => {
    Promise.all([api.get(`/catalogues/${id}`), api.get("/brand")])
      .then(([c, b]) => {
        setCat(c.data);
        setBrand(b.data);
      })
      .catch(() => {
        toast.error("Catalogue not found");
        navigate("/");
      });
    api.get("/covers").then((r) => setSavedCovers(r.data)).catch(() => {});
    runValidate();
    // eslint-disable-next-line
  }, [id]);

  const runValidate = useCallback(() => {
    api.get(`/catalogues/${id}/validate`).then((r) => setValidation(r.data)).catch(() => {});
  }, [id]);

  const persist = useCallback(async (data) => {
    setSaveState("saving");
    try {
      const { data: saved } = await api.put(`/catalogues/${id}`, data);
      setCat(saved);
      setSaveState("saved");
      runValidate();
    } catch (e) {
      setSaveState("dirty");
      toast.error("Autosave failed");
    }
  }, [id, runValidate]);

  const update = (patch) => {
    setCat((c) => {
      const next = { ...c, ...(typeof patch === "function" ? patch(c) : patch) };
      catRef.current = next;
      return next;
    });
    setSaveState("dirty");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(catRef.current), 1000);
  };

  const manualSave = () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    persist(catRef.current);
    toast.success("Saved");
  };

  const downloadFromPreview = async (kind = "whatsapp") => {
    await persist(catRef.current);
    await downloadExport(kind);
  };

  const shareFromPreview = async () => {
    await persist(catRef.current);
    await shareWhatsApp();
  };

  // ---------- products ----------
  const setProducts = (products) => update({ products });

  const addProduct = () => {
    const products = [...(cat.products || [])];
    if (products.length >= MAX_PRODUCTS) return toast.error(`Maximum ${MAX_PRODUCTS} products`);
    const np = { id: crypto.randomUUID(), position: products.length + 1 };
    setEditing({ product: np, index: products.length, isNew: true });
  };

  const saveProduct = (p) => {
    const products = [...(cat.products || [])];
    if (editing.isNew) products.push(p);
    else products[editing.index] = p;
    setProducts(products.map((x, i) => ({ ...x, position: i + 1 })));
    toast.success("Product saved");
  };

  const removeProduct = (i) => {
    const products = cat.products.filter((_, idx) => idx !== i).map((x, idx) => ({ ...x, position: idx + 1 }));
    setProducts(products);
  };

  const duplicateProduct = (i) => {
    if ((cat.products || []).length >= MAX_PRODUCTS) return toast.error(`Maximum ${MAX_PRODUCTS} products`);
    const clone = { ...cat.products[i], id: crypto.randomUUID() };
    const products = [...cat.products];
    products.splice(i + 1, 0, clone);
    setProducts(products.map((x, idx) => ({ ...x, position: idx + 1 })));
  };

  const move = (i, dir) => {
    const products = [...cat.products];
    const j = i + dir;
    if (j < 0 || j >= products.length) return;
    [products[i], products[j]] = [products[j], products[i]];
    setProducts(products.map((x, idx) => ({ ...x, position: idx + 1 })));
  };

  const onDrop = (i) => {
    if (dragIdx === null || dragIdx === i) return;
    const products = [...cat.products];
    const [moved] = products.splice(dragIdx, 1);
    products.splice(i, 0, moved);
    setProducts(products.map((x, idx) => ({ ...x, position: idx + 1 })));
    setDragIdx(null);
  };

  const toggleLibSel = (id) =>
    setLibSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const addSelectedFromLibrary = () => {
    const current = cat.products || [];
    const room = MAX_PRODUCTS - current.length;
    if (room <= 0) return toast.error(`Maximum ${MAX_PRODUCTS} products`);
    const picks = libView.filter((l) => libSel.has(l.id)); // keep the displayed order
    const toAdd = picks.slice(0, room);
    const skipped = picks.length - toAdd.length;
    const newProds = toAdd.map((lib) => {
      const { id: _drop, is_deleted, created_by, created_at, times_added, ...rest } = lib;
      return { ...rest, id: crypto.randomUUID(), library_id: lib.id };
    });
    const products = [...current, ...newProds].map((x, idx) => ({ ...x, position: idx + 1 }));
    setProducts(products);
    setLibOpen(false);
    setLibSel(new Set());
    toast.success(`Added ${toAdd.length} tea${toAdd.length > 1 ? "s" : ""}${skipped ? ` · ${skipped} skipped (${MAX_PRODUCTS}-product limit)` : ""}`);
  };

  const saveToLibrary = async (p) => {
    const { id: _i, position, ...rest } = p;
    try {
      await api.post("/products", rest);
      toast.success("Saved to product library");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save to library");
    }
  };

  const openLibrary = () => {
    api.get("/products").then((r) => setLibrary(r.data)).catch(() => {});
    setLibSel(new Set());
    setLibOpen(true);
  };

  const runBatch = async (fileList) => {
    const list = Array.from(fileList || []);
    if (!list.length) return;
    const remaining = MAX_PRODUCTS - (cat.products?.length || 0);
    if (remaining <= 0) return toast.error(`Maximum ${MAX_PRODUCTS} products reached`);
    const use = list.slice(0, remaining);
    if (list.length > remaining) toast(`Only ${remaining} of ${list.length} files will be added (${MAX_PRODUCTS}-product limit).`);
    setBatchBusy(true);
    setBatchProgress({ done: 0, total: use.length, phase: "upload" });
    try {
      const ups = [];
      for (let i = 0; i < use.length; i++) {
        ups.push(await uploadFile(use[i], "report"));
        setBatchProgress({ done: i + 1, total: use.length, phase: "upload" });
      }
      setBatchProgress({ done: use.length, total: use.length, phase: "read" });
      const { data } = await api.post("/ocr/batch", { file_ids: ups.map((u) => u.id) });
      const byId = Object.fromEntries(ups.map((u) => [u.id, u]));
      const newProducts = (data.results || []).map((r) => {
        const up = byId[r.file_id];
        return {
          id: crypto.randomUUID(),
          ...ocrToProductPatch(r.ocr || {}),
          report_file_id: up?.id,
          report_url: up?.url,
          report_content_type: up?.content_type,
          report_filename: up?.original_filename,
          match_status: r.match?.status,
          match_reasons: r.match?.reasons,
        };
      });
      const merged = [...(cat.products || []), ...newProducts].map((x, i) => ({ ...x, position: i + 1 }));
      setProducts(merged);
      toast.success(`Imported ${newProducts.length} product(s) from reports`);
      setBatchOpen(false);
    } catch (e) {
      toast.error("Batch import failed");
    } finally {
      setBatchBusy(false);
      setBatchProgress(null);
    }
  };

  // ---------- batch photos ----------
  const uploadOneLiquor = async (file) => {
    const d = await uploadFile(file, "liquor");
    return { liquor_image_file_id: d.id, liquor_image_url: d.url, liquor_image_width: d.width, liquor_image_height: d.height, liquor_image_content_type: d.content_type };
  };

  const openPhotos = () => {
    const need = (cat.products || []).filter((p) => !p.liquor_image_file_id)
      .map((p) => ({ id: p.id, position: p.position, name: p.garden || p.name || "Untitled tea", photo: null }));
    setPhotoDraft(need);
    setPhotoOpen(true);
  };

  const bulkFillPhotos = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type?.startsWith("image/"));
    if (!files.length) return;
    setPhotoBusy(true);
    try {
      const draft = [...photoDraft];
      let fi = 0;
      for (let i = 0; i < draft.length && fi < files.length; i++) {
        if (!draft[i].photo) { draft[i] = { ...draft[i], photo: await uploadOneLiquor(files[fi]) }; fi++; }
      }
      setPhotoDraft(draft);
      if (fi < files.length) toast(`${files.length - fi} extra photo(s) not used — every slot is filled.`);
    } catch (e) { toast.error("Some photos failed to upload"); }
    finally { setPhotoBusy(false); }
  };

  const setSlotPhoto = async (idx, file) => {
    if (!file) return;
    setPhotoBusy(true);
    try { const photo = await uploadOneLiquor(file); setPhotoDraft((d) => d.map((x, i) => (i === idx ? { ...x, photo } : x))); }
    catch (e) { toast.error("Upload failed"); }
    finally { setPhotoBusy(false); }
  };

  const applyPhotos = () => {
    const map = Object.fromEntries(photoDraft.filter((d) => d.photo).map((d) => [d.id, d.photo]));
    const filled = Object.keys(map).length;
    if (!filled) return;
    const merged = (cat.products || []).map((p) => (map[p.id] ? { ...p, ...map[p.id] } : p));
    setProducts(merged);
    toast.success(`Added ${filled} photo(s)`);
    setPhotoOpen(false);
  };

  // ---------- cover ----------
  // functional merge: always patches the *latest* cover (the AI placement result arrives async, after upload)
  const setCover = (patch) => update((c) => ({ cover: { ...(c.cover || {}), ...patch } }));

  const analyzeCoverPlacement = async (fileId, ctype) => {
    const fid = fileId || catRef.current?.cover?.file_id;
    if (!fid) return;
    toast.loading("AI is choosing the best spot for the number…", { id: "cover-ai" });
    try {
      const { data } = await api.post("/cover/analyze", { file_id: fid, content_type: ctype || catRef.current?.cover?.content_type });
      // ignore the result if the cover was changed again while analysing
      update((c) => (c.cover?.file_id === fid ? { cover: { ...c.cover, number_placement: data } } : {}));
      toast.success(`Number placed: ${(data.position || "top-center").replace("-", " ")}`, { id: "cover-ai" });
    } catch (e) {
      toast.error("Could not analyze cover — using top-center", { id: "cover-ai" });
    }
  };
  const COVER_DEFAULT_T = { rotate: 0, scale: 1, pos_x: 0, pos_y: 0, fit: "blur", safe_area: true };
  const setTransform = (patch) => update((c) => ({ cover: { ...(c.cover || {}), transform: { ...COVER_DEFAULT_T, ...(c.cover?.transform || {}), ...patch } } }));
  // merge into the latest number_placement (drag commits arrive async, after pointer-up)
  const patchPlacement = (patch) => update((c) => ({ cover: { ...(c.cover || {}), number_placement: { ...(c.cover?.number_placement || {}), ...patch } } }));

  const saveCoverToLibrary = async () => {
    if (!cat.cover?.file_id) return toast.error("Upload a cover first");
    try {
      const { data } = await api.post("/covers", {
        name: cat.title || "Cover", file_id: cat.cover.file_id, file_url: cat.cover.url, content_type: cat.cover.content_type, mode: cat.cover.mode || "background",
      });
      setSavedCovers((s) => [data, ...s]);
      toast.success("Cover saved to library");
    } catch (e) {
      toast.error("Could not save cover");
    }
  };

  // ---------- export & share ----------
  const downloadExport = async (kind) => {
    const map = {
      whatsapp: { url: `/catalogues/${id}/export/pdf?quality=whatsapp`, name: `${cat.catalogue_number}-whatsapp.pdf` },
      hq: { url: `/catalogues/${id}/export/pdf?quality=hq`, name: `${cat.catalogue_number}-hq.pdf` },
      jpg: { url: `/catalogues/${id}/export/jpg`, name: `${cat.catalogue_number}-pages.zip` },
    }[kind];
    toast.loading("Generating export…", { id: "exp" });
    try {
      const res = await api.get(map.url, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = map.name;
      a.click();
      URL.revokeObjectURL(blobUrl);
      toast.success("Export ready", { id: "exp" });
    } catch (e) {
      toast.error("Export failed", { id: "exp" });
    }
  };

  const shareWhatsApp = async () => {
    setShareOpen(true);
  };

  const prepareSharePdf = async () => {
    const res = await api.get(`/catalogues/${id}/export/pdf?quality=whatsapp`, { responseType: "blob" });
    return new File([res.data], `${cat.catalogue_number}.pdf`, { type: "application/pdf" });
  };

  const onShared = () => {
    setCat((c) => ({ ...c, status: "shared" }));
    api.post(`/catalogues/${id}/share`).catch(() => {});
  };

  const shareMsg = `Dear ${cat?.customer_name || "Customer"}, please find attached Prime Gold Tea Catalogue No. ${cat?.catalogue_number}. Kindly review the products and reply with the product numbers you would like to sample or discuss.`;

  const markReady = async () => {
    try {
      const { data } = await api.patch(`/catalogues/${id}/status`, { status: "ready" });
      setCat((c) => ({ ...c, status: data.status }));
      toast.success("Marked Ready to Share");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Complete required items first");
    }
  };

  const openVersions = () => {
    api.get(`/catalogues/${id}/versions`).then((r) => setVersions(r.data)).catch(() => {});
    setVersionsOpen(true);
  };

  const lockVersion = async () => {
    toast.loading("Locking version…", { id: "ver" });
    try {
      await api.post(`/catalogues/${id}/versions`);
      const { data } = await api.get(`/catalogues/${id}/versions`);
      setVersions(data);
      toast.success("Version locked", { id: "ver" });
    } catch (e) {
      toast.error("Could not lock version", { id: "ver" });
    }
  };

  const downloadVersion = async (v) => {
    try {
      const res = await api.get(`/catalogues/${id}/versions/${v.id}/download`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${cat.catalogue_number}-v${v.version_no}.pdf`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const doOverride = async () => {
    if (!overrideReason.trim()) return toast.error("Enter a reason");
    try {
      await api.post(`/catalogues/${id}/override`, { product_id: overrideFor.id, reason: overrideReason });
      const products = cat.products.map((x) =>
        x.id === overrideFor.id ? { ...x, match_status: "green", match_override: true, match_override_reason: overrideReason } : x
      );
      setProducts(products);
      toast.success("Override recorded");
      setOverrideFor(null);
      setOverrideReason("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not override");
    }
  };

  if (!cat || !brand) {
    return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#0B3B2E]" /></div>;
  }

  const products = cat.products || [];
  const missingPhotos = products.filter((p) => !p.liquor_image_file_id).length;
  const coverT = { ...COVER_DEFAULT_T, ...(cat.cover?.transform || {}) };

  return (
    <div className="pgt-fade-up space-y-6">
      {/* header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="font-mono-id text-sm font-bold text-[#C9A34A]" data-testid="editor-catalogue-number">{cat.catalogue_number}</div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#0B3B2E]">{cat.title || "Untitled Catalogue"}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-[#14513D]/60 w-16" data-testid="save-state">
            {saveState === "saving" ? "Saving…" : saveState === "dirty" ? "Unsaved" : "Saved"}
          </span>
          <Button variant="outline" onClick={openVersions} data-testid="versions-btn" className="gap-1"><History className="h-4 w-4" /> Versions</Button>
          <Button variant="outline" onClick={manualSave} data-testid="save-btn" className="gap-1"><Save className="h-4 w-4" /> Save</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-menu" className="gap-1"><Download className="h-4 w-4" /> Export</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Export catalogue</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => downloadExport("whatsapp")} data-testid="export-whatsapp"><MessageCircle className="h-4 w-4 mr-2" /> WhatsApp PDF (&lt;8 MB)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadExport("hq")} data-testid="export-hq"><FileDown className="h-4 w-4 mr-2" /> High-quality PDF</DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadExport("jpg")} data-testid="export-jpg"><Images className="h-4 w-4 mr-2" /> JPG pages (ZIP)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={shareWhatsApp} data-testid="share-btn" className="bg-[#25D366] hover:bg-[#1da851] text-white gap-1">
            <Share2 className="h-4 w-4" /> Share
          </Button>
        </div>
      </div>

      {/* validation banner */}
      {validation && (
        <Card className={`p-4 border ${validation.ready ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              {validation.ready ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <AlertTriangle className="h-5 w-5 text-amber-600" />}
              <span className="font-semibold text-[#0B3B2E]" data-testid="validation-headline">
                {validation.ready ? "Ready to share — all required items complete" : "Not ready — complete required items"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={cat.status === "ready" || cat.status === "shared" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-amber-100 text-amber-800 border-amber-300"}>
                Status: {cat.status}
              </Badge>
              <Button size="sm" onClick={markReady} disabled={!validation.ready} data-testid="mark-ready-btn" className="bg-[#0B3B2E] hover:bg-[#14513D]">
                Mark Ready
              </Button>
            </div>
          </div>
          <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
            {validation.checks.map((ch) => (
              <div key={ch.key} className="flex items-center gap-2 text-sm">
                {ch.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
                <span className={ch.ok ? "text-[#14513D]/70" : "text-[#14513D]"}>{ch.label}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Tabs defaultValue="info">
        <TabsList className="grid grid-cols-4 max-w-xl">
          <TabsTrigger value="info" data-testid="step-info">1 · Information</TabsTrigger>
          <TabsTrigger value="cover" data-testid="step-cover">2 · Cover</TabsTrigger>
          <TabsTrigger value="products" data-testid="step-products">3 · Products</TabsTrigger>
          <TabsTrigger value="preview" data-testid="step-preview">Preview</TabsTrigger>
        </TabsList>

        {/* STEP 1 INFO */}
        <TabsContent value="info" className="mt-6">
          <Card className="p-6 max-w-3xl">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Unique catalogue number</Label>
                <Input value={cat.catalogue_number} readOnly disabled className="font-mono-id" data-testid="info-number" />
              </div>
              <div className="space-y-1.5">
                <Label>Catalogue date</Label>
                <Input type="date" value={cat.date || ""} onChange={(e) => update({ date: e.target.value })} data-testid="info-date" />
              </div>
              <div className="space-y-1.5">
                <Label>Catalogue title</Label>
                <Input value={cat.title || ""} onChange={(e) => update({ title: e.target.value })} data-testid="info-title" />
              </div>
              <div className="space-y-1.5">
                <Label>Catalogue subtitle (optional)</Label>
                <Input value={cat.subtitle || ""} onChange={(e) => update({ subtitle: e.target.value })} data-testid="info-subtitle" />
              </div>
              <div className="space-y-1.5">
                <Label>Prepared by</Label>
                <Input value={cat.prepared_by || ""} onChange={(e) => update({ prepared_by: e.target.value })} data-testid="info-prepared" />
              </div>
              <div className="space-y-1.5">
                <Label>Sales executive</Label>
                <Input value={cat.sales_executive || ""} onChange={(e) => update({ sales_executive: e.target.value })} data-testid="info-executive" />
              </div>
              <div className="space-y-1.5">
                <Label>Customer name (optional)</Label>
                <Input value={cat.customer_name || ""} onChange={(e) => update({ customer_name: e.target.value })} data-testid="info-customer" />
              </div>
              <div className="space-y-1.5">
                <Label>WhatsApp number for chat buttons</Label>
                <Input value={cat.whatsapp_number || ""} onChange={(e) => update({ whatsapp_number: e.target.value })} placeholder={brand?.whatsapp || brand?.phone || "+91 …"} data-testid="info-whatsapp" />
                <p className="text-[11px] text-[#14513D]/60">Shown on the cover and in the footer of every page. Leave blank to use the brand number{brand?.whatsapp || brand?.phone ? ` (${brand.whatsapp || brand.phone})` : ""}.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Customer company (optional)</Label>
                <Input value={cat.customer_company || ""} onChange={(e) => update({ customer_company: e.target.value })} data-testid="info-company" />
              </div>
              <div className="space-y-1.5">
                <Label>Customer city (optional)</Label>
                <Input value={cat.customer_city || ""} onChange={(e) => update({ customer_city: e.target.value })} data-testid="info-city" />
              </div>
            </div>
            <Separator className="my-5" />
            <div className="flex flex-wrap gap-8">
              <label className="flex items-center gap-3">
                <Switch checked={!!cat.show_prices} onCheckedChange={(v) => update({ show_prices: v })} data-testid="info-show-prices" />
                <span className="text-sm font-medium text-[#14513D]">Show prices</span>
              </label>
              <label className="flex items-center gap-3">
                <Switch checked={!!cat.show_availability} onCheckedChange={(v) => update({ show_availability: v })} data-testid="info-show-availability" />
                <span className="text-sm font-medium text-[#14513D]">Show availability</span>
              </label>
              <label className="flex items-center gap-3">
                <Switch checked={!!cat.show_gst_inclusive} onCheckedChange={(v) => update({ show_gst_inclusive: v })} data-testid="info-show-gst-inclusive" />
                <span className="text-sm font-medium text-[#14513D]">Show GST-inclusive price</span>
              </label>
            </div>
          </Card>
        </TabsContent>

        {/* STEP 2 COVER */}
        <TabsContent value="cover" className="mt-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="font-display text-2xl text-[#0B3B2E] mb-1">Cover Page Design</h3>
              <p className="text-sm text-[#14513D]/70 mb-1">Portrait 4:5 · min 1080×1350 · JPG, PNG or PDF · max 20 MB. Your uploaded design is used as-is — only the catalogue number and the WhatsApp chat pill are overlaid.</p>
              <p className="text-xs font-semibold text-[#0B3B2E] mb-4" data-testid="cover-ratio-tip">For a perfect fit, design your cover at 4:5 (1080 × 1350 px).</p>

              <FileUpload
                label="Cover image / design"
                accept=".jpg,.jpeg,.png,.pdf"
                purpose="cover"
                testid="cover-upload"
                heightClass="h-72"
                minWidth={1080}
                aspectHint={0.8}
                value={cat.cover?.file_id ? { id: cat.cover.file_id, url: cat.cover.url, width: cat.cover.width, height: cat.cover.height, content_type: cat.cover.content_type, original_filename: cat.cover.filename } : null}
                onChange={(f) => {
                  setCover({
                    file_id: f?.id || null, url: f?.url || null, width: f?.width || null,
                    height: f?.height || null, content_type: f?.content_type || null, filename: f?.original_filename || null,
                    number_placement: null,
                  });
                  if (f?.id) analyzeCoverPlacement(f.id, f.content_type);
                }}
              />
              {cat.cover?.width && cat.cover?.height && Math.abs(cat.cover.width / cat.cover.height - 0.8) > 0.02 && (
                <p className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="cover-ratio-note">
                  This design is {cat.cover.width} × {cat.cover.height} px (not 4:5), so it is shown whole and uncut with softly blurred edges filling the page. Upload a 1080 × 1350 version for an exact edge-to-edge fit.
                </p>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                {cat.cover?.file_id && (
                  <Button variant="outline" size="sm" onClick={() => setCoverAdjust(true)} data-testid="cover-adjust-btn" className="gap-1"><Crop className="h-4 w-4" /> Crop / rotate / reposition</Button>
                )}
                {cat.cover?.file_id && (
                  <Button variant="outline" size="sm" onClick={() => analyzeCoverPlacement()} data-testid="cover-ai-btn" className="gap-1"><Sparkles className="h-4 w-4" /> AI place number</Button>
                )}
                <Button variant="outline" size="sm" onClick={saveCoverToLibrary} data-testid="save-cover-btn" className="gap-1"><ImageIcon className="h-4 w-4" /> Save cover for reuse</Button>
                {savedCovers.length > 0 && (
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="pick-saved-cover" className="gap-1"><Library className="h-4 w-4" /> Choose saved cover</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader><DialogTitle className="font-display text-2xl text-[#0B3B2E]">Saved Covers</DialogTitle></DialogHeader>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto">
                        {savedCovers.map((sc) => (
                          <button key={sc.id} onClick={() => { setCover({ file_id: sc.file_id, url: sc.file_url, content_type: sc.content_type, mode: sc.mode, number_placement: null }); analyzeCoverPlacement(sc.file_id, sc.content_type); }} className="rounded-lg border border-[#C9A34A]/30 overflow-hidden hover:shadow-md">
                            {sc.content_type === "application/pdf" ? (
                              <div className="aspect-[4/5] grid place-items-center bg-[#0B3B2E]/5 text-[10px] text-[#14513D]/60">PDF</div>
                            ) : (
                              <img src={fileUrl(sc.file_url)} alt={sc.name} className="aspect-[4/5] w-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
                            )}
                            <div className="text-xs p-2 truncate text-[#0B3B2E]">{sc.name}</div>
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-[#14513D]/60">Tip: re-upload if the preview does not show — saved covers reference stored files.</p>
                    </DialogContent>
                  </Dialog>
                )}
              </div>

              <div className="mt-4 rounded-lg bg-[#FAF7F0] border border-[#C9A34A]/25 p-3 text-xs text-[#14513D]/80">
                The catalogue number is placed on your cover by AI — it picks a clean, high-visibility spot (corner or edge) and matching text colour so your design stays clear. Tap “AI place number” to re-run, or Crop / rotate / reposition to fit the 4:5 page.
              </div>
            </Card>

            <div>
              <div className="text-sm font-semibold text-[#14513D] mb-2">Live cover preview</div>
              <div className="max-w-sm mx-auto shadow-2xl rounded-lg overflow-hidden border border-[#C9A34A]/30">
                <CoverPreview cat={cat} brand={brand} interactive onPlacementChange={patchPlacement} />
              </div>
              {cat.cover?.file_id && (
                <div className="max-w-sm mx-auto mt-3 space-y-2">
                  {cat.cover?.number_placement && (
                    <p className="text-xs text-[#14513D]/70" data-testid="cover-placement-reason">
                      <Sparkles className="h-3 w-3 inline mr-1 text-[#C9A34A]" />
                      {cat.cover.number_placement.reason && cat.cover.number_placement.reason !== "Placed manually"
                        ? `AI placed it ${(cat.cover.number_placement.position || "top-center").replace("-", " ")} — ${cat.cover.number_placement.reason}`
                        : `Number at ${(cat.cover.number_placement.position || "top-center").replace("-", " ")}${cat.cover.number_placement.reason === "Placed manually" ? " (manual)" : ""}`}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="text-[#14513D]/70">Number colour:</span>
                    <Button size="sm" variant={(cat.cover?.number_placement?.text_theme || "light") === "light" ? "default" : "outline"} onClick={() => setCover({ number_placement: { ...(cat.cover?.number_placement || {}), text_theme: "light" } })} data-testid="cover-theme-light" className="h-7 px-2">Light</Button>
                    <Button size="sm" variant={cat.cover?.number_placement?.text_theme === "dark" ? "default" : "outline"} onClick={() => setCover({ number_placement: { ...(cat.cover?.number_placement || {}), text_theme: "dark" } })} data-testid="cover-theme-dark" className="h-7 px-2">Dark</Button>
                    <label className="flex items-center gap-2 ml-1">
                      <Switch checked={!!cat.cover?.number_placement?.needs_backdrop} onCheckedChange={(v) => setCover({ number_placement: { ...(cat.cover?.number_placement || {}), needs_backdrop: v } })} data-testid="cover-backdrop" />
                      <span className="text-[#14513D]/70">Backdrop chip</span>
                    </label>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="text-[#14513D]/70">Number size:</span>
                    {["small", "medium", "large"].map((sz) => (
                      <Button key={sz} size="sm" variant={(cat.cover?.number_placement?.size || "medium") === sz ? "default" : "outline"} onClick={() => setCover({ number_placement: { ...(cat.cover?.number_placement || {}), size: sz } })} data-testid={`cover-size-${sz}`} className="h-7 px-2 capitalize">{sz}</Button>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="text-[#14513D]/70">WhatsApp button:</span>
                    {Object.entries(PILL_STYLES).map(([k, st]) => (
                      <Button key={k} size="sm" variant={(cat.cover?.number_placement?.chat_style || "green") === k ? "default" : "outline"} onClick={() => patchPlacement({ chat_style: k })} data-testid={`cover-chat-${k}`} className="h-7 px-2 gap-1.5">
                        <span className="inline-block h-3 w-3 rounded-full border border-black/10" style={{ background: st.bg }} /> {st.label}
                      </Button>
                    ))}
                  </div>
                  <p className="text-[11px] text-[#14513D]/50" data-testid="cover-drag-hint">
                    Drag the catalogue number or the WhatsApp button anywhere on the cover. Tap a dot to snap the number back to a corner, or hover it to see why the AI chose that spot.
                  </p>
                  {(cat.cover?.number_placement?.number_xy || cat.cover?.number_placement?.chat_xy) && (
                    <div className="flex flex-wrap gap-2 text-xs">
                      {cat.cover?.number_placement?.number_xy && (
                        <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => patchPlacement({ number_xy: null })} data-testid="cover-reset-number-spot">Reset number spot</Button>
                      )}
                      {cat.cover?.number_placement?.chat_xy && (
                        <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => patchPlacement({ chat_xy: null })} data-testid="cover-reset-chat-spot">Reset WhatsApp button spot</Button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* STEP 3 PRODUCTS */}
        <TabsContent value="products" className="mt-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="font-display text-2xl text-[#0B3B2E]">Products ({products.length}/{MAX_PRODUCTS})</h3>
              <p className="text-sm text-[#14513D]/70">Drag to reorder — numbers update automatically.</p>
            </div>
            <div className="flex gap-2">
              {missingPhotos > 0 && (
                <Button variant="outline" onClick={openPhotos} data-testid="batch-photos-btn" className="gap-1"><ImagePlus className="h-4 w-4" /> Add photos ({missingPhotos})</Button>
              )}
              <Button variant="outline" onClick={() => setBatchOpen(true)} disabled={products.length >= MAX_PRODUCTS} data-testid="batch-import-btn" className="gap-1"><Files className="h-4 w-4" /> Batch import</Button>
              <Button variant="outline" onClick={openLibrary} data-testid="add-from-library" className="gap-1"><Library className="h-4 w-4" /> From Library</Button>
              <Button onClick={addProduct} disabled={products.length >= MAX_PRODUCTS} data-testid="add-product-btn" className="bg-[#0B3B2E] hover:bg-[#14513D] gap-1"><Plus className="h-4 w-4" /> Add Product</Button>
            </div>
          </div>

          {products.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[#C9A34A]/40 bg-[#FAF7F0] py-16 text-center">
              <div className="text-[#14513D]/70">No products yet. Add between 1 and {MAX_PRODUCTS} tea products.</div>
            </div>
          ) : (
            <div className="space-y-3">
              {products.map((p, i) => (
                <Card
                  key={p.id}
                  draggable
                  onDragStart={() => setDragIdx(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(i)}
                  data-testid={`product-row-${i}`}
                  className="p-4 flex items-center gap-4 border-[#C9A34A]/25"
                >
                  <div className="cursor-grab text-[#C9A34A]"><GripVertical className="h-5 w-5" /></div>
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#0B3B2E] text-[#C9A34A] font-bold">{p.position}</div>
                  <div className="h-14 w-14 shrink-0 rounded-lg overflow-hidden bg-[#0B3B2E]/5 border border-[#C9A34A]/20 grid place-items-center">
                    {p.liquor_image_url ? <img src={fileUrl(p.liquor_image_url)} alt="" className="h-full w-full object-contain" /> : <ImageIcon className="h-5 w-5 text-[#C9A34A]/40" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[#0B3B2E] truncate">{p.garden || p.name || "Untitled tea"}</div>
                    <div className="text-xs text-[#14513D]/60 truncate">
                      {[p.grade, p.origin, ...(p.best_application || []).map((a) => APP_LABELS[a])].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <Badge variant="outline" className={fieldsComplete(p) ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-amber-100 text-amber-800 border-amber-300"}>
                    {fieldsComplete(p) ? "Ready" : "Incomplete"}
                  </Badge>
                  {p.match_status && (
                    <span className="flex items-center gap-1" title={(p.match_reasons || []).join(" ")} data-testid={`match-dot-${i}`}>
                      <span className={`h-2.5 w-2.5 rounded-full ${MATCH_DOT[p.match_status]?.cls}`} />
                      {p.match_override && <Lock className="h-3 w-3 text-[#14513D]/60" />}
                    </span>
                  )}
                  {p.match_status === "red" && !p.match_override && isAdmin && (
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => setOverrideFor(p)} data-testid={`override-${i}`} title="Override mismatch (admin)"><ShieldAlert className="h-4 w-4" /></Button>
                  )}
                  <div className="flex items-center gap-0.5">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`move-up-${i}`}><ChevronUp className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => move(i, 1)} disabled={i === products.length - 1} data-testid={`move-down-${i}`}><ChevronDown className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing({ product: p, index: i })} data-testid={`edit-product-${i}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => duplicateProduct(i)} data-testid={`dup-product-${i}`}><Copy className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => removeProduct(i)} data-testid={`remove-product-${i}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* PREVIEW */}
        <TabsContent value="preview" className="mt-6">
          <CataloguePreview cat={cat} brand={brand} onDownload={downloadFromPreview} onShare={shareFromPreview} />
        </TabsContent>
      </Tabs>

      {editing && (
        <ProductEditor
          open={!!editing}
          onOpenChange={(o) => !o && setEditing(null)}
          product={editing.product}
          onSave={saveProduct}
          onSaveToLibrary={saveToLibrary}
        />
      )}

      {/* batch import */}
      <Dialog open={batchOpen} onOpenChange={(o) => { if (!batchBusy) setBatchOpen(o); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl text-[#0B3B2E]">Batch import from reports</DialogTitle></DialogHeader>
          <p className="text-sm text-[#14513D]/70">Drop several tasting reports (JPG, PNG or PDF). Each report becomes a new product with fields auto-filled by the AI reader — add liquor photos afterwards.</p>
          <label
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (!batchBusy) runBatch(e.dataTransfer.files); }}
            className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed py-10 transition-colors ${batchBusy ? "border-[#C9A34A]/20 opacity-60" : "cursor-pointer border-[#C9A34A]/40 bg-[#FAF7F0] hover:border-[#C9A34A]/70"}`}
            data-testid="batch-dropzone"
          >
            {batchBusy ? <Loader2 className="h-8 w-8 animate-spin text-[#0B3B2E]" /> : <Files className="h-8 w-8 text-[#C9A34A]" />}
            <div className="text-sm font-semibold text-[#0B3B2E]">Drop reports here or click to choose</div>
            <div className="text-xs text-[#14513D]/60">Up to {Math.max(0, MAX_PRODUCTS - products.length)} more ({MAX_PRODUCTS}-product limit)</div>
            <input type="file" multiple accept=".jpg,.jpeg,.png,.pdf" className="hidden" disabled={batchBusy} onChange={(e) => runBatch(e.target.files)} data-testid="batch-input" />
          </label>
          {batchBusy && batchProgress && (
            <div className="text-sm text-[#14513D]" data-testid="batch-progress">
              {batchProgress.phase === "upload" ? "Uploading reports" : "Reading reports with AI"}… {batchProgress.done}/{batchProgress.total}
            </div>
          )}
          <DialogFooter><Button variant="ghost" onClick={() => setBatchOpen(false)} disabled={batchBusy} data-testid="batch-close">Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* batch photos */}
      <Dialog open={photoOpen} onOpenChange={(o) => { if (!photoBusy) setPhotoOpen(o); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl text-[#0B3B2E]">Add liquor photos</DialogTitle></DialogHeader>
          <p className="text-sm text-[#14513D]/70">Drop several photos to fill the empty slots below in order, or upload each one individually. A product becomes Ready once it has a photo.</p>
          <label
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (!photoBusy) bulkFillPhotos(e.dataTransfer.files); }}
            className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed py-8 transition-colors ${photoBusy ? "border-[#C9A34A]/20 opacity-60" : "cursor-pointer border-[#C9A34A]/40 bg-[#FAF7F0] hover:border-[#C9A34A]/70"}`}
            data-testid="photos-dropzone"
          >
            {photoBusy ? <Loader2 className="h-7 w-7 animate-spin text-[#0B3B2E]" /> : <ImagePlus className="h-7 w-7 text-[#C9A34A]" />}
            <div className="text-sm font-semibold text-[#0B3B2E]">Drop photos here to fill empty slots in order</div>
            <input type="file" multiple accept=".jpg,.jpeg,.png,.webp" className="hidden" disabled={photoBusy} onChange={(e) => bulkFillPhotos(e.target.files)} data-testid="photos-input" />
          </label>
          <div className="space-y-2">
            {photoDraft.length === 0 && <div className="text-sm text-[#14513D]/60 py-4 text-center">Every product already has a liquor photo.</div>}
            {photoDraft.map((d, idx) => (
              <div key={d.id} data-testid={`photo-slot-${idx}`} className="flex items-center gap-3 rounded-lg border border-[#C9A34A]/25 bg-[#FAF7F0] px-3 py-2">
                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#0B3B2E] text-[#C9A34A] font-bold text-sm">{d.position}</div>
                <div className="min-w-0 flex-1 font-semibold text-[#0B3B2E] truncate">{d.name}</div>
                <label className="h-14 w-14 shrink-0 rounded-lg overflow-hidden bg-white border border-[#C9A34A]/30 grid place-items-center cursor-pointer hover:border-[#C9A34A]">
                  {d.photo ? <img src={fileUrl(d.photo.liquor_image_url)} alt="" className="h-full w-full object-contain" /> : <ImagePlus className="h-5 w-5 text-[#C9A34A]/50" />}
                  <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" disabled={photoBusy} onChange={(e) => setSlotPhoto(idx, e.target.files?.[0])} data-testid={`photo-slot-input-${idx}`} />
                </label>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPhotoOpen(false)} disabled={photoBusy} data-testid="photos-cancel">Cancel</Button>
            <Button onClick={applyPhotos} disabled={photoBusy || !photoDraft.some((d) => d.photo)} className="bg-[#0B3B2E] hover:bg-[#14513D]" data-testid="photos-apply">Apply photos</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* library picker */}
      <Dialog open={libOpen} onOpenChange={setLibOpen}>        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl text-[#0B3B2E]">Add from Product Library</DialogTitle></DialogHeader>
          <div className="flex flex-wrap gap-3">
            <Select value={libGrade} onValueChange={setLibGrade}>
              <SelectTrigger className="w-44" data-testid="lib-picker-grade"><SelectValue placeholder="Grade" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All grades</SelectItem>
                {libGrades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={libSort} onValueChange={setLibSort}>
              <SelectTrigger className="w-48" data-testid="lib-picker-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="price_asc">Price: low → high</SelectItem>
                <SelectItem value="price_desc">Price: high → low</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="rounded-lg border border-[#C9A34A]/25 overflow-hidden divide-y divide-[#C9A34A]/15">
            <div className="grid grid-cols-[28px_1fr_70px_80px_100px] gap-2 bg-[#0B3B2E] text-[#C9A34A] text-[11px] uppercase tracking-wide font-semibold px-3 py-2">
              <div></div><div>Product</div><div>Grade</div><div>Price</div><div>Lot no.</div>
            </div>
            {libView.length === 0 && <div className="text-sm text-[#14513D]/60 py-6 text-center">{library.length === 0 ? "Library is empty." : "No teas match this grade."}</div>}
            {libView.map((lib) => (
              <div
                key={lib.id}
                onClick={() => toggleLibSel(lib.id)}
                data-testid={`lib-add-${lib.id}`}
                className={`cursor-pointer grid grid-cols-[28px_1fr_70px_80px_100px] gap-2 items-center px-3 py-2.5 transition-colors ${libSel.has(lib.id) ? "bg-[#C9A34A]/15" : "bg-[#FAF7F0] hover:bg-[#C9A34A]/10"}`}
              >
                <Checkbox checked={libSel.has(lib.id)} onCheckedChange={() => toggleLibSel(lib.id)} onClick={(e) => e.stopPropagation()} data-testid={`lib-check-${lib.id}`} />
                <div className="min-w-0">
                  <div className="font-semibold text-[#0B3B2E] truncate">{lib.brand_group || lib.garden || lib.name || "Untitled tea"}</div>
                  <div className="text-[11px] text-[#14513D]/60 truncate">{[lib.origin, lib.tea_category].filter(Boolean).join(" · ")}</div>
                </div>
                <div className="text-sm text-[#14513D] truncate">{lib.grade || "—"}</div>
                <div className="text-sm text-[#14513D] truncate">{lib.price_per_kg ? `₹${lib.price_per_kg}` : "—"}</div>
                <div className="text-sm text-[#14513D] truncate font-mono-id">{lib.lot_no || "—"}</div>
              </div>
            ))}
          </div>
          <DialogFooter className="items-center sm:justify-between gap-2">
            <span className="text-sm text-[#14513D]/70" data-testid="lib-picker-count">
              {libSel.size} selected · {(cat?.products || []).length}/{MAX_PRODUCTS} in catalogue
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setLibOpen(false)} data-testid="lib-picker-cancel">Cancel</Button>
              <Button onClick={addSelectedFromLibrary} disabled={libSel.size === 0} data-testid="lib-picker-add" className="bg-[#0B3B2E] hover:bg-[#14513D] gap-1">
                <Plus className="h-4 w-4" /> Add {libSel.size > 0 ? libSel.size : ""} to catalogue
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ShareSheet
        open={shareOpen}
        onOpenChange={setShareOpen}
        title={`Catalogue ${cat?.catalogue_number || ""} PDF`}
        message={shareMsg}
        prepare={prepareSharePdf}
        onShared={onShared}
      />

      {/* cover adjust */}
      <Dialog open={coverAdjust} onOpenChange={setCoverAdjust}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="font-display text-2xl text-[#0B3B2E]">Adjust Cover</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="max-w-[240px] mx-auto w-full shadow-xl rounded-lg overflow-hidden border border-[#C9A34A]/30">
              <CoverPreview cat={cat} brand={brand} />
            </div>
            <div className="space-y-5">
              <div>
                <Label className="text-xs font-semibold text-[#14513D]">Rotate</Label>
                <div className="mt-2 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setTransform({ rotate: ((coverT.rotate - 90) + 360) % 360 })} data-testid="cover-rotate-left" className="gap-1"><RotateCcw className="h-4 w-4" /> Left</Button>
                  <Button size="sm" variant="outline" onClick={() => setTransform({ rotate: (coverT.rotate + 90) % 360 })} data-testid="cover-rotate-right" className="gap-1"><RotateCw className="h-4 w-4" /> Right</Button>
                  <span className="text-sm text-[#14513D]/60 self-center">{coverT.rotate}°</span>
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#14513D]">Fit</Label>
                <div className="mt-2 flex gap-2">
                  <Button size="sm" variant={coverT.fit === "blur" ? "default" : "outline"} onClick={() => setTransform({ fit: "blur" })} data-testid="cover-fit-blur">Uncut + blurred edges</Button>
                  <Button size="sm" variant={coverT.fit === "cover" ? "default" : "outline"} onClick={() => setTransform({ fit: "cover" })} data-testid="cover-fit-cover">Fill &amp; crop</Button>
                  <Button size="sm" variant={coverT.fit === "contain" ? "default" : "outline"} onClick={() => setTransform({ fit: "contain" })} data-testid="cover-fit-contain">Uncut + green bars</Button>
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#14513D]">Zoom · {coverT.scale.toFixed(2)}×</Label>
                <Slider className="mt-2" value={[coverT.scale]} min={1} max={3} step={0.05} onValueChange={(v) => setTransform({ scale: v[0] })} data-testid="cover-zoom" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#14513D]">Reposition horizontal</Label>
                <Slider className="mt-2" value={[coverT.pos_x]} min={-100} max={100} step={1} onValueChange={(v) => setTransform({ pos_x: v[0] })} data-testid="cover-posx" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#14513D]">Reposition vertical</Label>
                <Slider className="mt-2" value={[coverT.pos_y]} min={-100} max={100} step={1} onValueChange={(v) => setTransform({ pos_y: v[0] })} data-testid="cover-posy" />
              </div>
              <label className="flex items-center gap-3">
                <Switch checked={coverT.safe_area} onCheckedChange={(v) => setTransform({ safe_area: v })} data-testid="cover-safearea" />
                <span className="text-sm font-medium text-[#14513D]">Show safe-area guide</span>
              </label>
              <Button variant="ghost" onClick={() => setCover({ transform: COVER_DEFAULT_T })} data-testid="cover-reset" className="gap-1"><RefreshCw className="h-4 w-4" /> Reset</Button>
            </div>
          </div>
          <DialogFooter><Button onClick={() => setCoverAdjust(false)} className="bg-[#0B3B2E] hover:bg-[#14513D]" data-testid="cover-adjust-done">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* version history */}
      <Dialog open={versionsOpen} onOpenChange={setVersionsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-display text-2xl text-[#0B3B2E]">Version History</DialogTitle></DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-[#14513D]/70">Locked versions preserve exactly what was exported or shared — re-sent catalogues stay unchanged.</p>
            <Button size="sm" onClick={lockVersion} data-testid="lock-version" className="bg-[#0B3B2E] hover:bg-[#14513D] gap-1 shrink-0"><Lock className="h-4 w-4" /> Lock current</Button>
          </div>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto">
            {versions.length === 0 && <div className="text-sm text-[#14513D]/60 py-6 text-center">No locked versions yet.</div>}
            {versions.map((v) => (
              <div key={v.id} data-testid={`version-${v.version_no}`} className="flex items-center gap-3 rounded-lg border border-[#C9A34A]/25 bg-[#FAF7F0] px-3 py-2">
                <div className="grid h-9 w-9 place-items-center rounded-full bg-[#0B3B2E] text-[#C9A34A] font-bold text-sm">v{v.version_no}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#0B3B2E]">{v.label}</div>
                  <div className="text-xs text-[#14513D]/60">{v.product_count} products · {v.quality} · {new Date(v.created_at).toLocaleString()} · {v.created_by_name}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => downloadVersion(v)} data-testid={`version-download-${v.version_no}`} className="gap-1"><Download className="h-3.5 w-3.5" /> PDF</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* admin override */}
      <Dialog open={!!overrideFor} onOpenChange={(o) => { if (!o) { setOverrideFor(null); setOverrideReason(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl text-[#0B3B2E]">Override mismatch</DialogTitle></DialogHeader>
          <p className="text-sm text-[#14513D]/70">Recording an administrator override for <span className="font-semibold">{overrideFor?.garden || overrideFor?.name}</span>. This is logged and stays internal — customers never see match status.</p>
          {(overrideFor?.match_reasons || []).length > 0 && (
            <ul className="list-disc list-inside text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {overrideFor.match_reasons.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          )}
          <Textarea value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="Reason for override…" data-testid="override-reason" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setOverrideFor(null); setOverrideReason(""); }}>Cancel</Button>
            <Button onClick={doOverride} className="bg-[#0B3B2E] hover:bg-[#14513D]" data-testid="override-confirm">Confirm override</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
